import React from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import StarRating from '../../library/star-rating/star-rating';
import Tooltip from '../../library/tooltip/tooltip';
import OptionCategory from './components/option-category/option-category';
import PriceRangeCategory from './components/price-range-category/price-range-category';
import LazyLoad from '../lazy-load/lazy-load';

import { getQueryParametersFromUrl } from '../../shared/utils/filters/filters';
import { getAccessoryCategoryType } from '../../shared/utils/product/product';
import { ProductListFiltersProp } from '../../shared/utils/prop-types/prop-types';

import './product-list-filter.scss';

function ProductListFilter(props) {
  const {
    accessoryCategory,
    filters = [],
    isAccessorySearch,
    isTextSearch,
    isLoadingPLP,
    isPrebuiltPackageSearch,
    defaultMaxPrice,
    defaultMinPrice,
    highestPriceInResults,
    lowestPriceInResults,
    onSelectFilter,
    onSliderValueChange,
    selectedFilters = [],
    shouldApplySliderOnChange = false,
    toggleType
  } = props;

  const location = useLocation();

  const { query } = getQueryParametersFromUrl(
    queryString.parse(location.search)
  );

  const hasSelectedPriceRangeFilter = Boolean(
    Array.isArray(selectedFilters) &&
      selectedFilters.find(
        selectedFilter =>
          selectedFilter.categoryCode === 'priceValue' ||
          selectedFilter.categoryCode === 'bundlePriceValue'
      )
  );

  return (
    <>
      {filters?.map(filter => {
        const filteredFilter = { ...filter };

        if (!isTextSearch && filter.categoryCode === 'quickFilters') {
          filteredFilter.values = filter.values.filter(
            v => v.categoryCode !== 'accessories-boolean'
          );
        }

        if (
          (isAccessorySearch &&
            !getAccessoryCategoryType(accessoryCategory) &&
            filteredFilter?.categoryCode === 'accessoryRootCategory') ||
          (isAccessorySearch &&
            getAccessoryCategoryType(accessoryCategory) &&
            filteredFilter?.categoryCode === 'accessoryCategory') ||
          (isPrebuiltPackageSearch &&
            filteredFilter?.categoryCode === 'allVehiclesRatingRange')
        ) {
          return;
        }

        const commonProps = {
          categoryName: filteredFilter?.categoryName,
          isOpenByDefault: filteredFilter?.isOpenByDefault
        };

        let category = null;

        if (
          filteredFilter.categoryName === 'Price Range' ||
          filteredFilter.categoryName === 'Package Price'
        ) {
          category = (
            <LazyLoad
              key={`${filteredFilter.categoryCode}_${query}`}
              placeholder={<div styleName="category-placeholder" />}
            >
              <PriceRangeCategory
                categoryCode={filteredFilter.categoryCode}
                defaultMaxPrice={defaultMaxPrice}
                defaultMinPrice={defaultMinPrice}
                description={filteredFilter.description}
                highestPriceInResults={highestPriceInResults}
                isLoadingPLP={isLoadingPLP}
                isSelected={hasSelectedPriceRangeFilter}
                key={`${filteredFilter.categoryCode}${
                  defaultMinPrice ? `_${defaultMinPrice}` : ''
                }${defaultMaxPrice ? `_${defaultMaxPrice}` : ''}`}
                lowestPriceInResults={lowestPriceInResults}
                onApply={onSelectFilter}
                onSliderValueChange={onSliderValueChange}
                shouldApplySliderOnChange={shouldApplySliderOnChange}
                toggleType={toggleType}
                {...commonProps}
              />
            </LazyLoad>
          );
        } else {
          category = (
            <LazyLoad
              key={`${filteredFilter.categoryCode}_${query}`}
              placeholder={<div styleName="category-placeholder" />}
            >
              <OptionCategory
                description={filteredFilter.description}
                filterValues={filteredFilter.values}
                hasTextFiltering={filteredFilter.categoryName.includes(
                  'Brands'
                )}
                onClick={onSelectFilter}
                selectedFilters={selectedFilters}
                showMoreByDefault={
                  filteredFilter.categoryName.includes('Brands')
                    ? false
                    : filteredFilter.showMoreByDefault
                }
                textFilteringPlaceholder={
                  filteredFilter.categoryName.includes('Brands')
                    ? 'Search Brand...'
                    : ''
                }
                toggleType={toggleType}
                {...commonProps}
              >
                {filterValue => {
                  const valueName =
                    filterValue.valueName?.toLowerCase() === 'in stock'
                      ? 'In Stock at My Store'
                      : filterValue.valueName;

                  return filteredFilter.categoryName === 'Ratings' ? (
                    <>
                      <StarRating
                        hideRating
                        rating={Number(filterValue.valueName?.charAt(0))}
                        styleName="stars"
                      />
                      {`${Number(filterValue.valueName?.charAt(0))} star`} (
                      {filterValue.count})
                    </>
                  ) : (
                    <>
                      {valueName} ({filterValue.count})
                      {filterValue.plpToolTip && (
                        <Tooltip
                          content={filterValue.plpToolTip}
                          styleName="tooltip"
                        />
                      )}
                    </>
                  );
                }}
              </OptionCategory>
            </LazyLoad>
          );
        }

        return category;
      })}
    </>
  );
}

ProductListFilter.propTypes = {
  accessoryCategory: PropTypes.string,
  defaultMaxPrice: PropTypes.number,
  defaultMinPrice: PropTypes.number,
  filters: ProductListFiltersProp,
  highestPriceInResults: PropTypes.number,
  isAccessorySearch: PropTypes.bool,
  isLoadingPLP: PropTypes.bool,
  isPrebuiltPackageSearch: PropTypes.bool,
  isTextSearch: PropTypes.bool,
  lowestPriceInResults: PropTypes.number,
  onSelectFilter: PropTypes.func,
  onSliderValueChange: PropTypes.func,
  selectedFilters: PropTypes.array,
  shouldApplySliderOnChange: PropTypes.bool,
  toggleType: PropTypes.string
};

export default ProductListFilter;
